import { h } from 'preact';
import { useState, useEffect, useCallback } from 'preact/hooks';
import { useAuth } from '../hooks/useAuth.js';
import { fileLinkClick } from '../utils/openFile.js';

const STATUS_LABELS = { geldig: 'Geldig', verlopen: 'Verlopen', in_aanvraag: 'In aanvraag' };
const STATUS_COLORS = { geldig: '#16a34a', verlopen: '#dc2626', in_aanvraag: '#d97706' };
const CAT_LABELS = { bhv: 'BHV', trainer: 'Trainer', algemeen: 'Algemeen' };

const WARN_DAYS = 60;

function daysBetween(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

function FileCell({ filePath, qualId, isAdmin, onChanged }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`/api/kwalificaties/${qualId}/upload`, { method: 'POST', body: fd });
    setUploading(false);
    if (res.ok) onChanged();
    else { const b = await res.json().catch(() => ({})); alert(b.error || 'Upload mislukt'); }
  };

  const handleDelete = async () => {
    if (!confirm('Bijlage verwijderen?')) return;
    await fetch(`/api/kwalificaties/${qualId}/upload`, { method: 'DELETE' });
    onChanged();
  };

  if (filePath) {
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <a href={`/api/kwalificaties/${qualId}/file`} target="_blank" rel="noopener noreferrer" onClick={fileLinkClick}
          style={{ fontSize: 12, color: 'var(--color-primary, #2563eb)', textDecoration: 'underline' }}>
          📎 Bekijk
        </a>
        {isAdmin && (
          <button onClick={handleDelete} style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>✕</button>
        )}
      </span>
    );
  }

  if (!isAdmin) return <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>—</span>;

  return (
    <label style={{ cursor: 'pointer', fontSize: 12, color: 'var(--color-primary, #2563eb)' }}>
      {uploading ? 'Uploaden...' : '+ Upload'}
      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp" style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
    </label>
  );
}

function StatusBadge({ status }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 12,
      fontSize: 12, fontWeight: 600, letterSpacing: '0.02em',
      background: STATUS_COLORS[status] + '22',
      color: STATUS_COLORS[status],
      border: `1px solid ${STATUS_COLORS[status]}44`
    }}>{STATUS_LABELS[status] || status}</span>
  );
}

function ExpiryCell({ expiryDate, status }) {
  if (!expiryDate) return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
  const days = daysBetween(expiryDate);
  const isExpired = days < 0;
  const isWarn = days >= 0 && days <= WARN_DAYS;
  const color = isExpired ? '#dc2626' : isWarn ? '#d97706' : 'var(--color-text)';
  return (
    <span style={{ color, fontWeight: isWarn || isExpired ? 600 : 400 }}>
      {expiryDate}
      {isWarn && !isExpired && <span style={{ fontSize: 11, marginLeft: 4 }}>({days}d)</span>}
      {isExpired && <span style={{ fontSize: 11, marginLeft: 4 }}>(verlopen)</span>}
    </span>
  );
}

// ── Register modal ────────────────────────────────────────────────────────────

function RegisterModal({ types, members, onSave, onClose, existing }) {
  const [form, setForm] = useState({
    user_id: existing?.user_id || '',
    type_id: existing?.type_id || '',
    obtained_date: existing?.obtained_date || '',
    expiry_date: existing?.expiry_date || '',
    status: existing?.status || 'geldig',
    notes: existing?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const selectedType = types.find(t => t.id === Number(form.type_id));

  // Auto-fill expiry if type has expiry_months and obtained_date filled
  useEffect(() => {
    if (!existing && selectedType?.requires_expiry && selectedType.expiry_months && form.obtained_date) {
      const d = new Date(form.obtained_date);
      d.setMonth(d.getMonth() + selectedType.expiry_months);
      setForm(f => ({ ...f, expiry_date: d.toISOString().slice(0, 10) }));
    }
  }, [form.obtained_date, form.type_id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.user_id || !form.type_id || !form.obtained_date) {
      setErr('Lid, kwalificatietype en behaaldatum zijn verplicht');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (ex) {
      setErr(ex.message || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const inp = { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '6px 10px', color: 'var(--color-text)', width: '100%', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--color-surface)', borderRadius: 12, padding: 28, width: 460, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin: '0 0 20px', color: 'var(--color-text)' }}>{existing ? 'Kwalificatie bewerken' : 'Kwalificatie registreren'}</h3>
        <form onSubmit={handleSubmit}>
          {!existing && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: 'var(--color-text-muted)' }}>Lid</label>
              <select style={inp} value={form.user_id} onChange={e => set('user_id', e.target.value)} required>
                <option value="">Kies een lid...</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
              </select>
            </div>
          )}
          {!existing && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: 'var(--color-text-muted)' }}>Kwalificatie</label>
              <select style={inp} value={form.type_id} onChange={e => set('type_id', e.target.value)} required>
                <option value="">Kies een kwalificatie...</option>
                {types.map(t => <option key={t.id} value={t.id}>{t.name} ({CAT_LABELS[t.category] || t.category})</option>)}
              </select>
            </div>
          )}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: 'var(--color-text-muted)' }}>Behaald op</label>
            <input type="date" style={inp} value={form.obtained_date} onInput={e => set('obtained_date', e.target.value)} required />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: 'var(--color-text-muted)' }}>
              Vervaldatum {selectedType?.requires_expiry ? '' : <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>(optioneel)</span>}
            </label>
            <input type="date" style={inp} value={form.expiry_date} onInput={e => set('expiry_date', e.target.value)} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: 'var(--color-text-muted)' }}>Status</label>
            <select style={inp} value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="geldig">Geldig</option>
              <option value="verlopen">Verlopen</option>
              <option value="in_aanvraag">In aanvraag</option>
            </select>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: 'var(--color-text-muted)' }}>Notities</label>
            <textarea style={{ ...inp, minHeight: 64, resize: 'vertical' }} value={form.notes} onInput={e => set('notes', e.target.value)} />
          </div>
          {err && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text)', cursor: 'pointer' }}>Annuleren</button>
            <button type="submit" disabled={saving} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: 'var(--color-primary, #2563eb)', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Opslaan...' : 'Opslaan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Type modal (nieuw + bewerken) ─────────────────────────────────────────────

function TypeModal({ existing, onSave, onClose }) {
  const [form, setForm] = useState({
    name: existing?.name || '',
    category: existing?.category || 'algemeen',
    requires_expiry: existing ? Boolean(existing.requires_expiry) : false,
    expiry_months: existing?.expiry_months || '',
    description: existing?.description || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '6px 10px', color: 'var(--color-text)', width: '100%', boxSizing: 'border-box' };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { setErr('Naam is verplicht'); return; }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (ex) {
      setErr(ex.message || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--color-surface)', borderRadius: 12, padding: 28, width: 400, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin: '0 0 20px', color: 'var(--color-text)' }}>{existing ? 'Type bewerken' : 'Nieuw kwalificatietype'}</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: 'var(--color-text-muted)' }}>Naam</label>
            <input style={inp} value={form.name} onInput={e => set('name', e.target.value)} required placeholder="bijv. EHBO" />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: 'var(--color-text-muted)' }}>Categorie</label>
            <select style={inp} value={form.category} onChange={e => set('category', e.target.value)}>
              <option value="bhv">BHV</option>
              <option value="trainer">Trainer</option>
              <option value="algemeen">Algemeen</option>
            </select>
          </div>
          <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" id="req_exp" checked={form.requires_expiry} onChange={e => set('requires_expiry', e.target.checked)} />
            <label for="req_exp" style={{ fontSize: 14, color: 'var(--color-text)' }}>Heeft vervaldatum</label>
          </div>
          {form.requires_expiry && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: 'var(--color-text-muted)' }}>Geldig voor (maanden)</label>
              <input type="number" style={inp} value={form.expiry_months} onInput={e => set('expiry_months', e.target.value)} placeholder="bijv. 24" />
            </div>
          )}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: 'var(--color-text-muted)' }}>Omschrijving</label>
            <input style={inp} value={form.description} onInput={e => set('description', e.target.value)} />
          </div>
          {err && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text)', cursor: 'pointer' }}>Annuleren</button>
            <button type="submit" disabled={saving} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: 'var(--color-primary, #2563eb)', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Opslaan...' : existing ? 'Opslaan' : 'Toevoegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main view ─────────────────────────────────────────────────────────────────

export default function KwalificatiesView() {
  const auth = useAuth();
  // Same rule as the server (services/qualification-access.js)
  const isAdmin = auth.isPlatformAdmin || auth.hasPermission('kwalificaties', 'update') || auth.hasPermission('users', 'update') || auth.hasPermission('settings', 'update');
  // Admins: the server's list (also members' own certificates and what expired last month)
  const [expiring, setExpiring] = useState(null);

  const [tab, setTab] = useState('overzicht');
  const [data, setData] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editType, setEditType] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [overviewRes, membersRes] = await Promise.all([
        fetch('/api/kwalificaties/overview?include_without=1'),
        isAdmin ? fetch('/api/kwalificaties/members') : Promise.resolve(null),
      ]);
      if (!overviewRes.ok) {
        const body = await overviewRes.json().catch(() => ({}));
        throw new Error(`Kon kwalificaties niet laden (${overviewRes.status}: ${body.error || 'onbekend'})`);
      }
      const overview = await overviewRes.json();
      setData(overview);
      if (isAdmin) {
        // Extra list: a failure here must not blank the page
        try {
          const expRes = await fetch('/api/kwalificaties/expiring');
          setExpiring(expRes.ok ? await expRes.json() : null);
        } catch { setExpiring(null); }
      }
      if (membersRes?.ok) setMembers(await membersRes.json());
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    let res;
    if (editRow) {
      res = await fetch(`/api/kwalificaties/${editRow.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form)
      });
    } else {
      res = await fetch('/api/kwalificaties', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form)
      });
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Opslaan mislukt');
    }
    setEditRow(null);
    await load();
  };

  const handleDelete = async (id) => {
    if (!confirm('Wil je deze registratie verwijderen?')) return;
    await fetch(`/api/kwalificaties/${id}`, { method: 'DELETE' });
    await load();
  };

  const handleSaveType = async (form) => {
    let res;
    if (editType) {
      res = await fetch(`/api/kwalificaties/types/${editType.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form)
      });
    } else {
      res = await fetch('/api/kwalificaties/types', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form)
      });
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Opslaan mislukt');
    }
    setEditType(null);
    await load();
  };

  const handleDeleteType = async (t) => {
    if (!confirm(`Wil je het type "${t.name}" permanent verwijderen?`)) return;
    const res = await fetch(`/api/kwalificaties/types/${t.id}`, { method: 'DELETE' });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      alert(body.error || 'Verwijderen mislukt');
      return;
    }
    await load();
  };

  const heading = { fontWeight: 700, color: 'var(--color-text)', margin: 0 };
  const card = { background: 'var(--color-surface)', borderRadius: 10, border: '1px solid var(--color-border)', padding: 20, marginBottom: 16 };
  const tabBtn = (t) => ({
    padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14,
    background: tab === t ? 'var(--color-primary, #2563eb)' : 'transparent',
    color: tab === t ? '#fff' : 'var(--color-text-muted)',
  });

  if (loading) return <div style={{ padding: 40, color: 'var(--color-text-muted)' }}>Laden...</div>;
  if (err) return <div style={{ padding: 40, color: '#dc2626' }}>{err}</div>;

  const types = data?.types || [];
  const qualified = data?.qualified || [];

  // Warn: expires within 60 days
  const warnings = qualified.filter(q => {
    if (!q.expiry_date || q.status !== 'geldig') return false;
    const d = daysBetween(q.expiry_date);
    return d !== null && d >= 0 && d <= WARN_DAYS;
  });

  const th = { padding: '8px 12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' };
  const td = { padding: '9px 12px', borderBottom: '1px solid var(--color-border)', fontSize: 14, color: 'var(--color-text)' };

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1100 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ ...heading, fontSize: 24, marginBottom: 4 }}>Kwalificaties</h1>
          <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: 14 }}>BHV-certificaten en trainersdiploma's van leden</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {isAdmin && (
            <a href="/api/kwalificaties/export.csv" data-native onClick={fileLinkClick} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text)', cursor: 'pointer', textDecoration: 'none', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
              ↓ Export CSV
            </a>
          )}
          {isAdmin && (
            <button onClick={() => { setEditRow(null); setShowModal(true); }} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--color-primary, #2563eb)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
              + Registreren
            </button>
          )}
        </div>
      </div>

      {/* Expiry warnings: admins get the full list from the server */}
      {isAdmin && expiring?.length > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '14px 18px', marginBottom: 20 }}>
          <div style={{ fontWeight: 700, color: '#92400e', fontSize: 14, marginBottom: 4 }}>⚠ Verloopt binnenkort of net verlopen ({expiring.length})</div>
          <div style={{ fontSize: 12, color: '#92400e', marginBottom: 8 }}>Leden krijgen zelf een herinnering ongeveer 2 maanden en 2 weken vooraf, en de dag na verlopen; beheerders krijgen dan een samenvatting.</div>
          {expiring.map(w => (
            <div key={`${w.source}:${w.id}`} style={{ fontSize: 13, color: w.expired ? '#991b1b' : '#78350f', marginBottom: 4 }}>
              {w.display_name} — {w.name}{w.source === 'certificate' ? ' (eigen certificaat)' : ''}: {w.expired ? `verlopen op ${w.expiry_date}` : `verloopt ${w.expiry_date} (nog ${daysBetween(w.expiry_date)} dagen)`}
            </div>
          ))}
        </div>
      )}
      {!isAdmin && warnings.length > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '14px 18px', marginBottom: 20 }}>
          <div style={{ fontWeight: 700, color: '#92400e', fontSize: 14, marginBottom: 8 }}>⚠ Verloopt binnenkort ({warnings.length})</div>
          {warnings.map(w => (
            <div key={w.id} style={{ fontSize: 13, color: '#78350f', marginBottom: 4 }}>
              {w.display_name} — {w.type_name}: verloopt {w.expiry_date} (nog {daysBetween(w.expiry_date)} dagen)
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'var(--color-surface-secondary, var(--color-surface))', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        <button style={tabBtn('overzicht')} onClick={() => setTab('overzicht')}>Overzicht per type</button>
        {isAdmin && <button style={tabBtn('typen')} onClick={() => setTab('typen')}>Typen beheren</button>}
      </div>

      {/* Tab: Overzicht per kwalificatietype */}
      {tab === 'overzicht' && (
        <div>
          {types.map(type => {
            const typeQuals = qualified.filter(q => q.type_id === type.id);
            const typeSelfCerts = (data?.selfCerts || []).filter(c => c.type_id === type.id);
            const geldig = typeQuals.filter(q => q.status === 'geldig');
            const verlopen = typeQuals.filter(q => q.status === 'verlopen');
            const aanvraag = typeQuals.filter(q => q.status === 'in_aanvraag');
            const selfBehaald = typeSelfCerts.filter(c => c.status === 'behaald');
            const selfVolgend = typeSelfCerts.filter(c => c.status === 'volgend');
            const withoutForType = (data?.without || []).filter(w => w.type_id === type.id);
            const hasAny = typeQuals.length > 0 || typeSelfCerts.length > 0;

            const SELF_STATUS_STYLE = {
              volgend:      { bg:'#eff6ff', border:'#93c5fd', color:'#1d4ed8', label:'Volgend' },
              behaald:      { bg:'#f0fdf4', border:'#86efac', color:'#15803d', label:'Behaald' },
              niet_behaald: { bg:'#fef2f2', border:'#fca5a5', color:'#b91c1c', label:'Niet behaald' },
            };

            return (
              <div key={type.id} style={card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--color-text)' }}>{type.name}</span>
                  <span style={{ fontSize: 12, color: 'var(--color-text-muted)', background: 'var(--color-border)', padding: '2px 8px', borderRadius: 8 }}>
                    {CAT_LABELS[type.category] || type.category}
                  </span>
                  {type.requires_expiry && <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>• vervalt na {type.expiry_months} mnd</span>}
                </div>

                {/* Summary counters */}
                <div style={{ display: 'flex', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, color: '#16a34a' }}>✓ {geldig.length} geldig (TC)</span>
                  {verlopen.length > 0 && <span style={{ fontSize: 13, color: '#dc2626' }}>✗ {verlopen.length} verlopen</span>}
                  {aanvraag.length > 0 && <span style={{ fontSize: 13, color: '#d97706' }}>⟳ {aanvraag.length} in aanvraag</span>}
                  {selfBehaald.length > 0 && <span style={{ fontSize: 13, color: '#15803d' }}>✓ {selfBehaald.length} behaald (zelf)</span>}
                  {selfVolgend.length > 0 && <span style={{ fontSize: 13, color: '#1d4ed8' }}>⟳ {selfVolgend.length} volgend (zelf)</span>}
                  {withoutForType.length > 0 && <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>— {withoutForType.length} niet geregistreerd</span>}
                </div>

                {hasAny && (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={th}>Naam</th>
                          <th style={th}>Bron</th>
                          <th style={th}>Behaald / Datum</th>
                          {type.requires_expiry && <th style={th}>Vervaldatum</th>}
                          <th style={th}>Status</th>
                          <th style={th}>Bijlage</th>
                          {isAdmin && <th style={th}></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {typeQuals.map(q => (
                          <tr key={`tc-${q.id}`}>
                            <td style={td}>{q.display_name}</td>
                            <td style={td}><span style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)' }}>TC</span></td>
                            <td style={td}>{q.obtained_date}</td>
                            {type.requires_expiry && <td style={td}><ExpiryCell expiryDate={q.expiry_date} status={q.status} /></td>}
                            <td style={td}><StatusBadge status={q.status} /></td>
                            <td style={td}><FileCell filePath={q.file_path} qualId={q.id} isAdmin={isAdmin} onChanged={load} /></td>
                            {isAdmin && (
                              <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                <button onClick={() => { setEditRow(q); setShowModal(true); }} style={{ fontSize: 12, color: 'var(--color-primary, #2563eb)', background: 'none', border: 'none', cursor: 'pointer', marginRight: 8 }}>Bewerk</button>
                                <button onClick={() => handleDelete(q.id)} style={{ fontSize: 12, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>Verwijder</button>
                              </td>
                            )}
                          </tr>
                        ))}
                        {typeSelfCerts.map(c => {
                          const sc = SELF_STATUS_STYLE[c.status] || SELF_STATUS_STYLE.behaald;
                          return (
                            <tr key={`sc-${c.id}`} style={{ background: 'var(--color-bg-secondary)' }}>
                              <td style={td}>{c.display_name}</td>
                              <td style={td}><span style={{ fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 8, background: '#ede9fe', color: '#7c3aed' }}>Zelf</span></td>
                              <td style={td}>{c.issued_date || '—'}</td>
                              {type.requires_expiry && <td style={td}>{c.expires_date || '—'}</td>}
                              <td style={td}><span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: sc.bg, border: `1px solid ${sc.border}`, color: sc.color }}>{sc.label}</span></td>
                              <td style={td}>{c.file_path ? (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <a href={`/api/profiles/certificates/${c.id}/file`} target="_blank" rel="noopener noreferrer" onClick={e => fileLinkClick(e, c.name)} style={{ fontSize: 12, color: 'var(--color-primary)' }}>📎 Bekijken</a>
                                  <a href={`/api/profiles/certificates/${c.id}/file?download=1`} data-native onClick={e => fileLinkClick(e, c.name)} style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>⬇ Download</a>
                                </span>
                              ) : '—'}</td>
                              {isAdmin && <td style={td} />}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {!hasAny && (
                  <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: 13 }}>Nog geen registraties voor dit type.</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Tab: Typen beheren */}
      {tab === 'typen' && isAdmin && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button onClick={() => { setEditType(null); setShowTypeModal(true); }} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--color-primary, #2563eb)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
              + Nieuw type
            </button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Naam</th>
                  <th style={th}>Categorie</th>
                  <th style={th}>Vervaldatum</th>
                  <th style={th}>Geldig voor</th>
                  <th style={th}>Omschrijving</th>
                  <th style={th}># registraties</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {types.map(t => (
                  <tr key={t.id}>
                    <td style={{ ...td, fontWeight: 600 }}>{t.name}</td>
                    <td style={td}>{CAT_LABELS[t.category] || t.category}</td>
                    <td style={td}>{t.requires_expiry ? 'Ja' : 'Nee'}</td>
                    <td style={td}>{t.expiry_months ? `${t.expiry_months} maanden` : '—'}</td>
                    <td style={{ ...td, color: 'var(--color-text-muted)' }}>{t.description || '—'}</td>
                    <td style={td}>{t.count_geldig + t.count_verlopen + t.count_aanvraag}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button onClick={() => { setEditType(t); setShowTypeModal(true); }} style={{ fontSize: 12, color: 'var(--color-primary, #2563eb)', background: 'none', border: 'none', cursor: 'pointer', marginRight: 8 }}>Bewerk</button>
                      <button onClick={() => handleDeleteType(t)} style={{ fontSize: 12, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>Verwijder</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {showModal && (
        <RegisterModal
          types={types}
          members={members}
          existing={editRow}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditRow(null); }}
        />
      )}
      {showTypeModal && (
        <TypeModal
          existing={editType}
          onSave={handleSaveType}
          onClose={() => { setShowTypeModal(false); setEditType(null); }}
        />
      )}
    </div>
  );
}
